import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import { api, setCsrfToken, AUTH_BASE } from '../lib/api'

export interface User {
  id: string // UUID from backend
  email: string
  full_name: string | null
  status: string
  is_admin: boolean
  created_at: string
  updated_at: string
}

interface AuthContextValue {
  user: User | null
  loading: boolean
  csrfToken: string | null
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string) => Promise<{ message: string }>
  logout: () => Promise<void>
  checkStatus: (email: string) => Promise<{ status: string } | null>
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  const checkStatus = useCallback(
    async (email: string) => {
      try {
        const res = await api.get<{ status: string }>(`${AUTH_BASE}/check-status`, { params: { email } })
        return res.data
      } catch {
        return null
      }
    },
    [],
  )

  const login: AuthContextValue['login'] = useCallback(
    async (email: string, password: string) => {
      const res = await api.post<{
        user: User
        access_token: string
        csrf_token: string
      }>(`${AUTH_BASE}/login`, { email, password })
      const data = res.data
      setUser(data.user)
      setCsrfToken(data.csrf_token)
    },
    [],
  )

  const register: AuthContextValue['register'] = useCallback(
    async (name: string, email: string, password: string) => {
      const res = await api.post<{ message: string }>(`${AUTH_BASE}/register`, { name, email, password })
      return res.data
    },
    [],
  )

  const logout = useCallback(async () => {
    try {
      await api.post(`${AUTH_BASE}/logout`)
    } catch {
      // Backend clears cookies anyway; ignore network errors.
    }
    setUser(null)
  }, [])

  // On mount, validate the session via /v0.1/auth/me (uses the httpOnly cookie
  // set by the browser). If the cookie is valid, the endpoint returns the user.
  useEffect(() => {
    let cancelled = false
    api
      .get<User>(`${AUTH_BASE}/me`)
      .then((res) => {
        if (!cancelled) setUser(res.data)
      })
      .catch(() => {
        // Not authenticated — leave user null.
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, csrfToken: null, login, register, logout, checkStatus }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return ctx
}
